/**
 * Shared tournament constants and helpers.
 * Used across BoutsTab, BoutDetail, BoutFormModal, RingDisplay, CheckinTab, BoutPivotAnalysis.
 */
import type { RegistrationWeighinDay, WeighinStatus } from '../types';
import { todayLocal } from './calendarDate';

/** #599 — a bout seats 3 to 5 side judges (IFMA v3.057 §21.6). The backend
 *  holds the same maximum (`MAX_SIDE_JUDGES`, services/officials.ts); a round
 *  with fewer than 3 cards needs the jury's confirmation. */
export const MIN_SIDE_JUDGES = 3;
export const MAX_SIDE_JUDGES = 5;

/** #615 — the colours of a stage pill: the live rows of the tournament card
 *  and the bout history of the athlete page. Another stage (R32, Challenge)
 *  is grey. */
export const STAGE_PILL_CLS: Record<string, string> = {
  'Final': 'bg-amber-100 text-amber-800 font-bold',
  'SF':    'bg-purple-100 text-purple-800',
  'QF':    'bg-blue-100  text-blue-800',
  'R16':   'bg-gray-100  text-gray-700',
};

/** #600 — the round time and the rest time of each division, in seconds:
 *  IFMA v3.057 Rule 7 (every division has 3 rounds; the Masters rest 1.5
 *  minutes) and v2.04 Rule 4 for the legacy codes (1 minute of rest). The
 *  timer of the jury reads the round time, and the bout form the rest.
 *  `roundFormula()` below writes the round time on the screens from the same
 *  row, so a class has one row and no second copy (F56, #661).
 *  ifma-round-times-v3.spec.ts pins the v3 rows to Rule 7. A class with no
 *  row runs on Elite. */
export const ROUND_TIMING: Record<string, { roundSec: number; restSec: number }> = {
  // legacy v2.04
  'Senior':  { roundSec: 180, restSec: 60 },
  'Senior+': { roundSec: 180, restSec: 60 },
  '23U':     { roundSec: 180, restSec: 60 },
  '16-17':   { roundSec: 120, restSec: 60 },
  '14-15':   { roundSec: 120, restSec: 60 },
  '12-13':   { roundSec: 90,  restSec: 60 },
  '10-11':   { roundSec: 60,  restSec: 60 },
  '8-9':     { roundSec: 60,  restSec: 60 },
  // IFMA v3.057
  'U8':          { roundSec: 60,  restSec: 60 },
  'U10':         { roundSec: 60,  restSec: 60 },
  'U12':         { roundSec: 60,  restSec: 60 },
  'U14':         { roundSec: 90,  restSec: 60 },
  'U16':         { roundSec: 120, restSec: 60 },
  'U18':         { roundSec: 120, restSec: 60 },
  'U24':         { roundSec: 180, restSec: 60 },
  'Elite':       { roundSec: 180, restSec: 60 },
  'Masters 35+': { roundSec: 180, restSec: 90 },
  'Masters 40+': { roundSec: 120, restSec: 90 },
  'Masters 45+': { roundSec: 120, restSec: 90 },
};
/** #602 — the rounds of a bout: 3, as Rule 7 gives every division, or up to
 *  `max_rounds` of its class (`AgeGroup.max_rounds`: 5 for U24, Elite and the
 *  Masters, an LMF option, operator 2026-10-14). */
export const DEFAULT_ROUNDS = 3;
/** The rounds of a bout. A row from a backend before #602 has none: 3. */
export const boutRounds = (bout: { rounds?: number | null }) => bout.rounds ?? DEFAULT_ROUNDS;
/** Seconds as m:ss, for example 90 gives "1:30". */
const mmss = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
/** The round formula of a bout, for example "5 × 3:00"; null for an unknown class
 *  (the timer of the jury runs an unknown class on Elite, the text names no time). */
export function roundFormula(ageGroup: string | null | undefined, rounds?: number | null): string | null {
  const timing = ageGroup ? ROUND_TIMING[ageGroup] : undefined;
  return timing ? `${rounds ?? DEFAULT_ROUNDS} × ${mmss(timing.roundSec)}` : null;
}

/** #592 — IFMA v3.057 Rule 31.3, the restricted strikes of a division
 *  (`docs/ifma-rules-reference-v3.md`). A restricted strike is a foul. U16 and
 *  older have no restriction. The v3 codes only (operator, 2026-10-11): a
 *  past bout with a v2.04 code shows no badge, because Rule 27.3 of v2.04
 *  differs (14-15 had a restriction, U16 has none). */
export const HEAD_STRIKE_RULE: Record<string, 'strikesNoHead' | 'strikesNoKneeElbowHead'> = {
  U8: 'strikesNoHead',
  U10: 'strikesNoHead',
  U12: 'strikesNoHead',
  U14: 'strikesNoKneeElbowHead',
};

/** Age groups in chronological order (youngest → oldest), INTERLEAVED across
 *  legacy v2.04 and v3.057 by min_age. Critical: deriveAgeGroup(a, b) below
 *  picks the OLDER corner's code via this position, so cross-ruleset bouts
 *  (mixed legacy + v3 corners — possible via historical-tournament edits)
 *  must compare consistently. Sorting by min_age handles this — Senior(min 18)
 *  > U18(min 16) regardless of ruleset. Ties preserve legacy-first.
 *
 *  Must mirror backend/src/lib/ifmaAge.ts AG_ORDER (single source of truth). */
export const AG_ORDER = [
  'U8',          // min 6
  '8-9',         // min 8
  'U10',         // min 8
  '10-11',       // min 10
  'U12',         // min 10
  '12-13',       // min 12
  'U14',         // min 12
  '14-15',       // min 14
  'U16',         // min 14
  '16-17',       // min 16
  'U18',         // min 16
  '23U',         // min 17
  'Senior',      // min 18
  'U24',         // min 18
  'Elite',       // min 18
  'Masters 35+', // min 35
  'Senior+',     // min 40
  'Masters 40+', // min 40
  'Masters 45+', // min 45
];

/** The v3 age groups: [code, min, max]. Mirrors AG_RANGES_V3 in
 *  backend/src/lib/ifmaAge.ts; `age-group-order-parity.spec.ts` pins the pair.
 *  The list order matters where the ranges overlap: the Masters groups come
 *  before Elite, so an athlete of 35-40 derives Masters. Elite comes before
 *  U24, so an athlete of 18-23 derives Elite on every tournament type
 *  (operator, 2026-10-08, #554; IFMA §15.4: LMF uses no body protector from
 *  18). */
export const AG_RANGES_V3: ReadonlyArray<readonly [string, number, number]> = [
  ['U8',          6,  7],
  ['U10',         8,  9],
  ['U12',         10, 11],
  ['U14',         12, 13],
  ['U16',         14, 15],
  ['U18',         16, 17],
  ['Masters 45+', 45, 50],
  ['Masters 40+', 40, 44],
  ['Masters 35+', 35, 39],
  ['Elite',       18, 40],
  ['U24',         18, 23],
];

/** #532 — the v3 codes that a tournament can allow, in AG_ORDER order. */
export const V3_AGE_GROUP_CODES: readonly string[] =
  AG_ORDER.filter(code => AG_RANGES_V3.some(([c]) => c === code));

/** #532 — a tournament takes this age group: no list (every age group), or
 *  the list names it. */
export function isAgeGroupAllowed(code: string, allowed: readonly string[] | null | undefined): boolean {
  return !allowed || allowed.includes(code);
}

/** The v3 age group of an athlete, from the YEAR of birth (IFMA v3.057
 *  §5.1.1 + §5.1.2: competition year minus birth year, no birthday). Mirrors
 *  `deriveAgeGroup()` in backend/src/lib/ifmaAge.ts, the `allowed` rule
 *  included (#532): when the default age group is not allowed, the first
 *  allowed age group in AG_ORDER order that the athlete is eligible for.
 *  `competitionYear` is a number, for example `Number(date_start.slice(0, 4))`.
 *  It is never a `Date` that was made from a day string (#410, #664). */
export function deriveAgeGroupFromYear(
  birthYear: number | null,
  competitionYear: number,
  allowed?: readonly string[] | null,
): string | null {
  if (!birthYear) return null;
  const age = competitionYear - birthYear;
  for (const [code, min, max] of AG_RANGES_V3) {
    if (age >= min && age <= max) {
      if (isAgeGroupAllowed(code, allowed)) return code;
      break;
    }
  }
  if (!allowed) return null;
  for (const code of V3_AGE_GROUP_CODES) {
    if (!allowed.includes(code)) continue;
    const range = AG_RANGES_V3.find(([c]) => c === code);
    if (range && age >= range[1] && age <= range[2]) return code;
  }
  return null;
}

/** Position of an age-group code in AG_ORDER. An unknown code ranks 99, after
 *  every known code, exactly as `ageGroupCaseSql()` does on the backend. */
export function ageGroupRank(code?: string | null): number {
  if (!code) return 99;
  const i = AG_ORDER.indexOf(code);
  return i === -1 ? 99 : i;
}

export function compareAgeGroups(a?: string | null, b?: string | null): number {
  return ageGroupRank(a) - ageGroupRank(b);
}

export function compareWeightClasses(a?: string | null, b?: string | null): number {
  return parseWcWeight(a || '') - parseWcWeight(b || '');
}

/** The one order for a category list: gender, then age group, then weight
 *  class. Every grouped list (the Boxers tab, the bout-form athlete picker,
 *  the bout pivot) sorts with this function. No component holds an order of
 *  its own — a private map in the Boxers tab knew only the v2.04 codes and
 *  sorted every v3 tournament by weight alone (#344). */
export interface CategoryLike {
  gender?: string | null;
  ageGroup?: string | null;
  weightClass?: string | null;
}
export function compareCategories(a: CategoryLike, b: CategoryLike): number {
  const ga = (a.gender || '').toLowerCase(), gb = (b.gender || '').toLowerCase();
  if (ga !== gb) return ga < gb ? -1 : 1;
  return compareAgeGroups(a.ageGroup, b.ageGroup) || compareWeightClasses(a.weightClass, b.weightClass);
}

/** Derive the higher (older) age group from two corners */
export function deriveAgeGroup(a?: string, b?: string): string {
  if (!a && !b) return '';
  if (!a) return b!;
  if (!b) return a;
  if (a === b) return a;
  return ageGroupRank(a) >= ageGroupRank(b) ? a : b;
}

/** Parse weight class label to numeric value for comparison */
export function parseWcWeight(label: string): number {
  if (label.startsWith('+')) return 9999;
  return parseFloat(label.replace('-', '')) || 0;
}

/** The weight class for an athlete weight (#458): the lowest capped class
 *  whose limit is at least the weight, else the open class ('+91'). The same
 *  rule as GET /api/reference/suggest-weight-class. It sorts first: the two
 *  copies it replaces (Check-in, Register Boxers) relied on the order of the
 *  list they got. `null` when the weight is not a positive number or no
 *  class fits. */
export function suggestWeightClass(kg: number | undefined, labels: string[]): string | null {
  if (kg == null || !Number.isFinite(kg) || kg <= 0 || !labels.length) return null;
  const sorted = [...labels].sort((a, b) => parseWcWeight(a) - parseWcWeight(b));
  return sorted.find(l => parseWcWeight(l) >= kg) ?? null;
}

/** Derive the higher weight class from two corners */
export function deriveWeightClass(a?: string, b?: string): string {
  if (!a && !b) return '';
  if (!a) return b!;
  if (!b) return a;
  if (a === b) return a;
  return parseWcWeight(a) >= parseWcWeight(b) ? a : b;
}

/** #661, F11 — the weigh-in mark of a registration on one day. The list route
 *  sends the stored days only, so a day with no row is `pending`. `day` is the
 *  `YYYY-MM-DD` of the day, or a date string that starts with it. The Check-in
 *  tab and the label of its tab count read the same rule. */
export function weighinStatusOn(
  reg: { weighin_days?: readonly RegistrationWeighinDay[] | null },
  day: string,
): WeighinStatus {
  const found = reg.weighin_days?.find(d => d.date?.startsWith(day));
  return found?.status ?? 'pending';
}

/** #664, F29 — where a tournament stands against a day: `today` when the day
 *  is inside the range (the end defaults to the start), `upcoming` when the
 *  start comes after the day, else `past`. Calendar days compare as strings
 *  (#410). `today` defaults to the day of the browser. */
export type TournamentPhase = 'today' | 'upcoming' | 'past';
export function tournamentPhase(
  t: { date_start: string; date_end?: string | null },
  today: string = todayLocal(),
): TournamentPhase {
  const start = String(t.date_start).slice(0, 10);
  const end = t.date_end ? String(t.date_end).slice(0, 10) : start;
  if (start > today) return 'upcoming';
  if (end < today) return 'past';
  return 'today';
}

/** #664, F29 — a copy of the list in the order of `date_start`. The days
 *  compare as strings, so no `Date` is made from a day (#410). A tie keeps
 *  the order of the list. */
export function sortByStart<T extends { date_start: string }>(list: readonly T[], dir: 'asc' | 'desc'): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    const x = String(a.date_start).slice(0, 10);
    const y = String(b.date_start).slice(0, 10);
    return x < y ? -sign : x > y ? sign : 0;
  });
}
